'use client'

import React, { useState } from 'react'
import { X, RotateCcw, Download, Eye, MoreVertical } from 'lucide-react'
import { cn } from '../../../utils/styles'
import { formatFileSize, getFileTypeCategory } from '../utils/fileUtils'
import { FileThumbnail } from './FileThumbnail'
import { UploadProgressBar } from './UploadProgress'
import { InlineValidation } from './ValidationMessages'
import type { FileItemProps, FileWithUIState } from '../types'

export function FileItem({
  file,
  index,
  showThumbnail = true,
  thumbnailSize = 48,
  showFileInfo = true,
  showProgress = true,
  onRemove,
  onRetry,
  className,
  variant = 'default'
}: FileItemProps) {
  const [showActions, setShowActions] = useState(false)

  const isUploading = file.status === 'uploading'
  const isCompleted = file.status === 'completed'
  const hasError = file.status === 'error'
  const isPending = file.status === 'pending'

  const getStatusColor = () => {
    switch (file.status) {
      case 'completed':
        return 'border-green-200 bg-green-50'
      case 'error':
        return 'border-red-200 bg-red-50'
      case 'uploading':
        return 'border-blue-200 bg-blue-50'
      case 'pending':
      default:
        return 'border-gray-200 bg-white'
    }
  }

  const getStatusIndicator = () => {
    switch (file.status) {
      case 'completed':
        return <div className="w-2 h-2 bg-green-500 rounded-full" />
      case 'error':
        return <div className="w-2 h-2 bg-red-500 rounded-full" />
      case 'uploading':
        return <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
      case 'pending':
      default:
        return <div className="w-2 h-2 bg-gray-300 rounded-full" />
    }
  }

  if (variant === 'compact') {
    return <FileItemCompact {...{ file, index, onRemove, onRetry, className }} />
  }

  if (variant === 'gallery') {
    return <FileItemGallery {...{ file, index, thumbnailSize, onRemove, onRetry, className }} />
  }

  return (
    <div
      className={cn(
        'group relative flex items-center space-x-3 p-3 border rounded-lg transition-all duration-200',
        getStatusColor(),
        'hover:shadow-sm',
        className
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Status indicator */}
      <div className="flex-shrink-0">
        {getStatusIndicator()}
      </div>

      {/* Thumbnail */}
      {showThumbnail && (
        <div className="flex-shrink-0">
          <FileThumbnail
            file={file}
            size={thumbnailSize}
            className="border border-gray-200 rounded"
          />
        </div>
      )}

      {/* File info */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 truncate pr-2">
            {file.name}
          </h4>

          {/* Actions */}
          <div className={cn(
            'flex items-center space-x-1 transition-opacity',
            showActions || isUploading || hasError ? 'opacity-100' : 'opacity-0'
          )}>
            {hasError && onRetry && (
              <button
                type="button"
                onClick={() => onRetry(file, index)}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
                title="Retry upload"
                aria-label="Retry upload"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}

            {isCompleted && file.result?.url && (
              <>
                <button
                  type="button"
                  onClick={() => window.open(file.result.url, '_blank')}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
                  title="View file"
                  aria-label="View file"
                >
                  <Eye className="w-4 h-4" />
                </button>

                <a
                  href={file.result.url}
                  download={file.name}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
                  title="Download file"
                  aria-label="Download file"
                >
                  <Download className="w-4 h-4" />
                </a>
              </>
            )}

            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(file, index)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded"
                title="Remove file"
                aria-label="Remove file"
                disabled={isUploading}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {showFileInfo && (
          <div className="flex items-center space-x-3 text-xs text-gray-500">
            <span>{formatFileSize(file.size)}</span>
            <span className="capitalize">{getFileTypeCategory(file)}</span>
            {isCompleted && file.result?.uploadTime && (
              <span>{(file.result.uploadTime / 1000).toFixed(1)}s</span>
            )}
          </div>
        )}

        {/* Progress */}
        {showProgress && isUploading && file.progress !== undefined && (
          <div className="mt-2">
            <UploadProgressBar
              progress={{
                percentage: file.progress,
                loaded: (file.size * file.progress) / 100,
                total: file.size
              }}
              variant="minimal"
              showDetails={false}
            />
          </div>
        )}

        {/* Error message */}
        {hasError && file.error && (
          <div className="mt-1">
            <p className="text-xs text-red-600">{file.error}</p>
          </div>
        )}

        {/* Success message */}
        {isCompleted && (
          <div className="mt-1">
            <p className="text-xs text-green-600">Upload completed</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Compact variant for smaller spaces
function FileItemCompact({
  file,
  index,
  onRemove,
  onRetry,
  className
}: {
  file: FileWithUIState
  index: number
  onRemove?: (file: FileWithUIState, index: number) => void
  onRetry?: (file: FileWithUIState, index: number) => void
  className?: string
}) {
  const isUploading = file.status === 'uploading'
  const hasError = file.status === 'error'

  return (
    <div className={cn(
      'group flex items-center justify-between p-2 border rounded text-sm',
      {
        'border-green-200 bg-green-50': file.status === 'completed',
        'border-red-200 bg-red-50': hasError,
        'border-blue-200 bg-blue-50': isUploading,
        'border-gray-200 bg-white': file.status === 'pending'
      },
      className
    )}>
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <div className="w-2 h-2 rounded-full flex-shrink-0" />
        <span className="truncate font-medium">{file.name}</span>
        <span className="text-xs text-gray-500 flex-shrink-0">
          {formatFileSize(file.size)}
        </span>
      </div>

      <div className="flex items-center space-x-1 ml-2">
        {hasError && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(file, index)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded"
            title="Retry"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        )}

        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(file, index)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded opacity-0 group-hover:opacity-100"
            title="Remove"
            disabled={isUploading}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  )
}

// Gallery variant for image-focused layouts
function FileItemGallery({
  file,
  index,
  thumbnailSize = 120,
  onRemove,
  onRetry,
  className
}: {
  file: FileWithUIState
  index: number
  thumbnailSize?: number
  onRemove?: (file: FileWithUIState, index: number) => void
  onRetry?: (file: FileWithUIState, index: number) => void
  className?: string
}) {
  const [showOverlay, setShowOverlay] = useState(false)
  const hasError = file.status === 'error'
  const isUploading = file.status === 'uploading'

  return (
    <div
      className={cn('group relative', className)}
      onMouseEnter={() => setShowOverlay(true)}
      onMouseLeave={() => setShowOverlay(false)}
    >
      {/* Thumbnail */}
      <div className="relative">
        <FileThumbnail
          file={file}
          size={thumbnailSize}
          className="rounded-lg shadow-sm"
        />

        {/* Status overlay */}
        <div className={cn(
          'absolute inset-0 flex items-center justify-center rounded-lg transition-opacity',
          {
            'bg-red-500 bg-opacity-75': hasError,
            'bg-blue-500 bg-opacity-75': isUploading,
            'bg-green-500 bg-opacity-75': file.status === 'completed',
            'bg-black bg-opacity-50': showOverlay && file.status === 'pending'
          }
        )}>
          {hasError && (
            <div className="text-white text-center">
              <X className="w-6 h-6 mx-auto mb-1" />
              <p className="text-xs">Failed</p>
            </div>
          )}

          {isUploading && file.progress !== undefined && (
            <div className="text-white text-center">
              <div className="text-lg font-bold">{file.progress.toFixed(0)}%</div>
            </div>
          )}

          {file.status === 'completed' && (
            <div className="text-white text-center">
              <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-1">
                ✓
              </div>
              <p className="text-xs">Done</p>
            </div>
          )}
        </div>

        {/* Actions overlay */}
        {showOverlay && (file.status === 'pending' || file.status === 'completed') && (
          <div className="absolute top-2 right-2 flex space-x-1">
            {hasError && onRetry && (
              <button
                type="button"
                onClick={() => onRetry(file, index)}
                className="p-1 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-70 transition-colors"
                title="Retry upload"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            )}

            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(file, index)}
                className="p-1 bg-black bg-opacity-50 text-white rounded hover:bg-opacity-70 transition-colors"
                title="Remove file"
                disabled={isUploading}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* File name */}
      <div className="mt-2 px-1">
        <p className="text-xs text-gray-700 truncate font-medium" title={file.name}>
          {file.name}
        </p>
        <p className="text-xs text-gray-500">
          {formatFileSize(file.size)}
        </p>

        {hasError && file.error && (
          <p className="text-xs text-red-600 mt-1 truncate" title={file.error}>
            {file.error}
          </p>
        )}
      </div>
    </div>
  )
}

// File item with drag handle for reordering
export interface DraggableFileItemProps extends FileItemProps {
  isDragging?: boolean
  dragHandleProps?: any
}

export function DraggableFileItem({
  isDragging,
  dragHandleProps,
  className,
  ...props
}: DraggableFileItemProps) {
  return (
    <div
      className={cn(
        'transition-all duration-200',
        isDragging && 'opacity-50 scale-105 shadow-lg',
        className
      )}
    >
      <div className="flex items-center space-x-2">
        {/* Drag handle */}
        <div
          {...dragHandleProps}
          className="flex flex-col space-y-0.5 p-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
          title="Drag to reorder"
        >
          <div className="w-1 h-1 bg-current rounded-full" />
          <div className="w-1 h-1 bg-current rounded-full" />
          <div className="w-1 h-1 bg-current rounded-full" />
          <div className="w-1 h-1 bg-current rounded-full" />
          <div className="w-1 h-1 bg-current rounded-full" />
          <div className="w-1 h-1 bg-current rounded-full" />
        </div>

        {/* File item */}
        <div className="flex-1">
          <FileItem {...props} className="" />
        </div>
      </div>
    </div>
  )
}